import { CompanyProductSelection } from '@iota/iota-management-portal-v4-client';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import {
    AddUpdateRemoveCompanyProductSelection,
    AddUpdateRemoveCompanyProductSelectionAttributes,
    AddUpdateRemoveCompanyProductSelectionSuccess,
    ClearLatestCompanyProductSelectionIds,
    CompanyProductSelectionActionFailure,
    LoadCompanyProductSelectionsByCompany,
    LoadCompanyProductSelectionsFailure,
    LoadCompanyProductSelectionsSuccess,
    ReloadCompanyProductSelectionsByIds,
    ReloadCompanyProductSelectionsByIdsSuccess,
    SaveLatestCompanyProductSelectionIdsAndLoadCompanyProductSelectionsByCompany
} from '../actions/company-product-selection.actions';
import { AppState } from '../store';

export interface CompanyProductSelectionState extends EntityState<CompanyProductSelection> {
    loading: boolean;
    actionLoading: boolean;
    error: any;
    loadedCompanies: string[];

    // Last upserted companyProductSelectionIds, used to update the registrationPeriod
    latestCompanyProductSelectionIds: string[];
}

function selectCompanyProductSelectionId(a: CompanyProductSelection): string {
    return a.companyProductSelectionId;
}

export const companyProductSelectionAdapter: EntityAdapter<CompanyProductSelection> = createEntityAdapter<CompanyProductSelection>({
    selectId: selectCompanyProductSelectionId
});

export const initialState: CompanyProductSelectionState = companyProductSelectionAdapter.getInitialState({
    loading: false,
    actionLoading: false,
    error: undefined,
    loadedCompanies: [],
    latestCompanyProductSelectionIds: []
});

export const companyProductSelectionReducer = createReducer(
    initialState,
    on(LoadCompanyProductSelectionsByCompany, (state) => ({ ...state, loading: true })),
    on(LoadCompanyProductSelectionsSuccess, (state, { productSelections, companyId }) =>
        companyProductSelectionAdapter.upsertMany(
            productSelections || [],
            companyProductSelectionAdapter.removeMany((s) => s.companyId === companyId, {
                ...state,
                loading: false,
                actionLoading: false,
                loadedCompanies: [...state.loadedCompanies, companyId]
            })
        )
    ),

    on(AddUpdateRemoveCompanyProductSelectionAttributes, (state) => ({ ...state, actionLoading: true })),

    on(ReloadCompanyProductSelectionsByIds, (state) => ({ ...state, actionLoading: true })),
    on(ReloadCompanyProductSelectionsByIdsSuccess, (state, { productSelections }) =>
        companyProductSelectionAdapter.upsertMany(productSelections || [], {
            ...state,
            actionLoading: false
        })
    ),
    on(LoadCompanyProductSelectionsFailure, (state, error) => ({ ...state, loading: false, actionLoading: false, error })),
    on(AddUpdateRemoveCompanyProductSelection, (state) => ({ ...state, actionLoading: true })),
    on(AddUpdateRemoveCompanyProductSelectionSuccess, (state) => ({ ...state, actionLoading: false })),
    on(CompanyProductSelectionActionFailure, (state, error) => ({ ...state, actionLoading: false, error })),

    on(SaveLatestCompanyProductSelectionIdsAndLoadCompanyProductSelectionsByCompany, (state, { latestCompanyProductSelectionIds }) => ({
        ...state,
        latestCompanyProductSelectionIds
    })),
    on(ClearLatestCompanyProductSelectionIds, (state) => ({
        ...state,
        latestCompanyProductSelectionIds: []
    }))
);

const { selectAll } = companyProductSelectionAdapter.getSelectors();

export const companyProductSelectionState = (state: AppState) => state.companyProductSelectionState;
export const allCompanyProductSelections = createSelector(companyProductSelectionState, selectAll);
export const productSelectionsByCompanyId = (companyId: string) =>
    createSelector(allCompanyProductSelections, (selections) => selections.filter((elem) => elem.companyId === companyId));
export const productSelectionsByIds = (ids: string[]) =>
    createSelector(allCompanyProductSelections, (selections) => selections.filter((elem) => ids.includes(elem.companyProductSelectionId)));
export const productSelectionsLoadedByCompany = (companyId: string) =>
    createSelector(companyProductSelectionState, (state) => state.loadedCompanies.includes(companyId));
export const companyProductSelectionLoading = createSelector(companyProductSelectionState, (state) => state.loading);
export const companyProductSelectionActionLoading = createSelector(companyProductSelectionState, (state) => state.actionLoading);
export const latestCompanyProductSelectionIds = createSelector(
    companyProductSelectionState,
    (state) => state.latestCompanyProductSelectionIds
);
